import { Fragment, useEffect, useRef, useState } from 'react';

import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';

import { TemplateInvoiceService as Service } from '../../../service/invoice/TemplateInvoiceService';
import { TypeInvoiceService } from '../../../service/invoice/TypeInvoiceService';

import { useToolbar } from '../../../shared/hooks/Toolbar';
import { useDataTable } from '../../../shared/hooks/DataTable';
import { useDialogCrup } from '../../../shared/hooks/DialogCrup';
import { useTranslation } from '../../../shared/language';

export const List = () => {
  const {t} = useTranslation();

  const header = t('templatePreview.template');
  const dataKey = 'id';

  const preview = useRef(null);

  const [displayPreview, setDisplayPreview] = useState(false);
  const [displayDownload, setDisplayDownload] = useState(false);
  const [detail, setDetail] = useState(null);

  const [invoiceTypes, setInvoiceTypes] = useState([]);

  const pageSizes = [
    { value: 'A4', label: 'A4' },
    { value: 'A5', label: 'A5' },
    { value: 'A6', label: 'A6' },
    { value: 'A7', label: 'A7' }
  ];
  const pageOrientations = [
    { value: 'Portrait', label: 'Portrait' },
    { value: 'Landscape', label: 'Landscape' }
  ];

  const typeCompanyOptions = [
    { value: 0, label: t('common.normal') },
    { value: 1, label: t('common.petroleum') },
    { value: 2, label: t('common.jewelry') },
    { value: 3, label: t('common.airline') },
    { value: 4, label: t('common.vehicleSales') },
    { value: 5, label: t('common.transportation') },
    { value: 6, label: t('common.digitalTransportation') },
  ];

  useEffect(() => {
    TypeInvoiceService.getList({}).then(({ items }) => {
      if (items) {
        const _invoiceTypes = [];
        items.forEach((item) => {
          _invoiceTypes.push({ value: item.id, label: item.name });
        });
        setInvoiceTypes(_invoiceTypes);
      }
    });
  }, []);

  const { render: renderDataTable, refreshLazyData, reloadLazyData, selectedItems } = useDataTable({
    header,
    dataKey,
    columns: [
      { field: 'code', header: t('common.templateCode'), width: 150, matchMode: 'contains' },
      { field: 'name', header: t('common.templateName'), minWidth: 250, matchMode: 'contains' },
      { field: 'typeInvoiceId', header: t('common.invoiceType'), width: 180, matchMode: 'contains', filterType: 'dropdown', filterOptions: invoiceTypes, dataType: 'fromFilterOptions' },
      { field: 'pageSize', header: t('common.paperSize'), width: 120, matchMode: 'contains' },
      { field: 'pageOrientation', header: t('common.paperOrientation'), width: 120, matchMode: 'contains' },
      { field: 'numberCharacterInLine', header: t('common.charactersPerLine'), width: 125, dataType: 'number' },
      { field: 'ratioConvertUpperCase', header: t('common.caseConversion'), width: 160, dataType: 'number' },
      { field: 'numberRecordMutile', header: t('common.linesPerPage'), width: 160, dataType: 'number' },
      { field: 'numberRecord', header: t('common.blankLines'), width: 125, dataType: 'number' },
      { field: 'isShowOrder', header: t('common.showSequence'), width: 100, dataType: 'checkbox' },
      { field: 'fileName', header: t('common.htmlFile'), width: 220, matchMode: 'contains' },
      { field: 'imagePrivew', header: t('common.image'), width: 150, matchMode: 'contains' },
      { field: 'creatorName', header: t('common.creator'), width: 150, matchMode: 'contains' },
      { field: 'creationTime', header: t('common.createdDate'), width: 110, matchMode: 'contains', dataType: 'date' }
    ],
    indexColumnWidth: 40,
    getList: (params) => {
      return Service.getList(params);
    }
  });

  useEffect(() => {
    if (selectedItems && selectedItems['id']) {
      Service.get(selectedItems['id']).then((data) => {
        setDetail(data);
      });
    }
  }, [selectedItems]);

  const makeFormData = (item: any) => {
    const formData = new FormData();
    Object.keys(item).forEach((key) => {
      if (['html', 'image'].includes(key)) {
        if (item[key]) {
          formData.append('files', item[key]);
        }
      } else {
        formData.append(key, item[key]);
      }
    });
    return formData;
  }

  const { render: renderDialogCrup, create, update, clone } = useDialogCrup({
    header,
    dataKey,
    width: '80rem',
    fields: [
      { field: 'code', header: t('common.templateCode'), required: true, uppercase: true, disabled: 'onlyUpdate', InputTextProps: { maxLength: 50 }, className: 'md:col-4' },
      { field: 'name', header: t('common.templateName'), required: true, InputTextProps: { maxLength: 250 }, className: 'md:col-8' },
      { field: 'typeInvoiceId', header: t('common.invoiceType'), required: true, type: 'Dropdown', DropdownProps: { options: invoiceTypes, filter: true }, className: 'md:col-6' },
      { field: 'pageSize', header: t('common.paperSize'), required: true, type: 'Dropdown', DropdownProps: { options: pageSizes }, className: 'md:col-3' },
      { field: 'pageOrientation', header: t('common.paperOrientation'), required: true, type: 'Dropdown', DropdownProps: { options: pageOrientations }, className: 'md:col-3' },
      { field: 'typeCompany', header: t('common.companyType'), required: true, type: 'Dropdown', DropdownProps: { options: typeCompanyOptions }, className: 'md:col-12' },
      { field: 'numberCharacterInLine', header: t('common.charactersPerLine'), required: true, className: 'md:col-3' },
      { field: 'ratioConvertUpperCase', header: t('common.caseConversion'), required: true, className: 'md:col-3' },
      { field: 'numberRecordMutile', header: t('common.linesPerPage'), required: true, className: 'md:col-3' },
      { field: 'numberRecord', header: t('common.blankLines'), required: true, className: 'md:col-3' },
      { field: 'isShowOrder', header: t('common.showSequence'), type: 'Checkbox', className: 'md:col-12' },
      { field: 'html', header: t('common.htmlFile'), required: 'onlyCreate', type: 'file', InputFileProps: { accept: '.html' }, className: 'md:col-6' },
      { field: 'image', header: t('common.imageFile'), required: 'onlyCreate', type: 'file', InputFileProps: { accept: 'image/*' }, className: 'md:col-6' }
    ],
    createItem: (item) => {
      return Service.create(makeFormData(item));
    },
    updateItem: (id, item) => {
      return Service.update(id, makeFormData(item));
    },
    refreshLazyData
  });

  const { renderToolbar } = useToolbar({
    dataKey, create, update, clone, reloadLazyData, selectedItems, otherActions: [
      {
        label: t('common.previewFiles'), icon: 'pi pi-fw pi-images', className: 'iam-toolbar iam-toolbar-help', command: () => {
          if (selectedItems && selectedItems['id']) {
            setDisplayPreview(true);
            Service.get(selectedItems['id']).then((data) => {
              setDetail(data);
              preview.current.contentWindow.document.open();
              preview.current.contentWindow.document.write(`${data.fileContent.trim()}`);
            });
          }
        }
      },
      {
        label: t('common.download'), icon: 'pi pi-fw pi-images', className: 'iam-toolbar iam-toolbar-help', command: () => {
          if (selectedItems && selectedItems['id']) {
            setDetail(selectedItems);
            setDisplayDownload(true);
          }
        }
      }
    ], deleteItem: (id) => {
      return Service.delete(id);
    }
  });

  const downloadHtml = () => {
    if (detail && detail.id) {
      Service.getHtml(detail.id).then((data) => {
        const link = document.createElement('a');
        link.href = `data:text/plain;charset=utf-8,${encodeURIComponent(data)}`;
        link.download = `${detail.fileName}`;
        link.click();
      });
    }
  }

  const downloadImagePreview = () => {
    if (detail && detail.id) {
      Service.getImagePreview(detail.id).then((data) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(new Blob([data]));
        link.download = `${detail.imagePrivew}`;
        link.click();
      });
    }
  }

  const windowHeight = window.innerHeight;

  return (
    <Fragment>
      {renderToolbar()}
      <div className="grid m-0">
        <div className="col-9 p-0">
          {renderDataTable()}
        </div>
        <div className="col-3">
          <div className="card">
            {(detail && detail['imageContent']) &&
              <img src={`data:image/png;base64,${detail['imageContent']}`} alt="" width="100%" />
            }
          </div>
        </div>
      </div>
      {renderDialogCrup()}
      <Dialog header={t('common.previewFiles')} visible={displayPreview} style={{ width: '1300px' }} position="top" onHide={() => setDisplayPreview(false)}>
        <div className="grid">
          <div className="surface-card col iam-scroll" style={{ height: (windowHeight - 240) + 'px', overflow: 'hidden auto' }}>
            {(detail && detail['imageContent']) &&
              <img src={`data:image/png;base64,${detail['imageContent']}`} alt="" width="100%" />
            }
          </div>
          <div className="surface-card col-fixed iam-scroll" style={{ width: '850px', height: (windowHeight - 240) + 'px', overflow: 'hidden auto' }}>
            <iframe className="template-preview" ref={preview} title="template-preview" />
          </div>
        </div>
        <div className="flex justify-content-end">
          <Button label={t('common.close')} icon="pi pi-times" onClick={() => setDisplayPreview(false)} className="p-button-sm p-button-raised p-button-text p-button-plain" />
        </div>
      </Dialog>
      <Dialog header={t('common.download')} visible={displayDownload} style={{ width: '40rem' }} position="top" onHide={() => setDisplayDownload(false)}>
        {detail &&
          <div className="p-fluid mt-3">
            <div className="field grid">
              <label className="col-12 mb-2 md:col-2 md:mb-0">{t('common.htmlFile')}</label>
              <div className="col-12 md:col-10">
                <Button icon="pi pi-download" iconPos="right" label={detail.fileName} className="p-button-outlined p-button-secondary"
                  onClick={downloadHtml} />
              </div>
            </div>
            <div className="field grid">
              <label className="col-12 mb-2 md:col-2 md:mb-0">{t('common.imageFile')}</label>
              <div className="col-12 md:col-10">
                <Button icon="pi pi-download" iconPos="right" label={detail.imagePrivew} className="p-button-outlined p-button-secondary"
                  onClick={downloadImagePreview} />
              </div>
            </div>
          </div>
        }
        <div className="flex justify-content-end">
          <Button label={t('common.close')} icon="pi pi-times" onClick={() => setDisplayDownload(false)} className="p-button-sm p-button-raised p-button-text p-button-plain" />
        </div>
      </Dialog>
    </Fragment>
  )
}
